import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import { uniq } from 'lodash';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ModelEntityType } from '../../../../../../../../../../common/model/input/types';
import { FinalImpactScoreType, NetScoreFormat } from '../../../../../../../../../../common/model/output/scores/types';
import { getValueSetName } from '../../../../../../../../../untidy/redux/selectors';
import { bouTextFontStyle } from '../../../../../../../../generic/content/texts/bou-text/text-style';
import { BouColor } from '../../../../../../../../generic/global/bou-colors';
import { scoreTextColor } from '../../../../../../../../generic/utilities/format';
import { NetScoreTypeAndValueSet } from '../../../../bou-net-score-type-and-value-set';
import { useImpactChartContext, useImpactChartFontSizes, useImpactChartWidths } from '../../../context';
import { formatNetScore } from '../../../value-formatter';
import { NetScoreRowProps } from './types';

const NetScoreRowRightPartContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  left: 30px;
  top: 50%;
  bottom: 50%;
  position: absolute;
`;

const ScoreText = styled.text`
  ${bouTextFontStyle};
  font-weight: bold;
  text-anchor: middle;
`;

function NetScoreRowRightPart(props: NetScoreRowProps) {
  const { valueSetName } = props;
  const { data, relativeMode, staticMode, desktopMode, netScoreFormat, language } = useImpactChartContext();
  const widths = useImpactChartWidths();
  const { scoreTypeText: fontSize } = useImpactChartFontSizes();

  // the description of the value set should discuss the relevant
  // entity type. but if there are entities of different types, fall
  // back to ModelEntityType.COMPANY
  const entityTypes = uniq(data.map(d => d.type));
  const entityTypeForValueSet = (entityTypes.length === 1 && entityTypes[0]) || ModelEntityType.COMPANY;

  const x = desktopMode ? widths.title + widths.bars + widths.middle + 20 : widths.titleTextPadding;
  const type = relativeMode ? FinalImpactScoreType.RELATIVE : FinalImpactScoreType.ABSOLUTE;

  const scoreFormat = relativeMode ? netScoreFormat : NetScoreFormat.NET_SUM;

  return (
    <NetScoreRowRightPartContainer style={{ left: x, fontSize }}>
      <NetScoreTypeAndValueSet
        valueSetName={valueSetName}
        hasTooltip={!staticMode}
        type={type}
        entityType={entityTypeForValueSet}
        netScoreFormat={scoreFormat}
        language={language}
      />
    </NetScoreRowRightPartContainer>
  );
}

function NetScoreRowMiddlePart(props: NetScoreRowProps) {
  const { formatters, scoreType, netScoreFormat, negativeColor, positiveColor, backgroundColor } =
    useImpactChartContext();
  const activeValueSetName = useSelector(getValueSetName);
  const widths = useImpactChartWidths();
  const { netScore: fontSize } = useImpactChartFontSizes();
  const boxWidth = 70;
  const boxHeight = fontSize * 1.65; // TODO
  const isActive = props.valueSetName === activeValueSetName;

  if (!props.value) {
    return <g />;
  }

  // TODO: Add spinner to tell if scores are updating. UpdateJobStatus is already in the context.

  const formattedScore = formatNetScore(props.value, netScoreFormat, formatters, scoreType);

  return (
    <g transform={`translate(${widths.title + widths.bars + widths.middle / 2}, ${boxHeight / 2})`}>
      <rect
        width={boxWidth}
        height={boxHeight}
        y={-boxHeight / 2}
        x={-boxWidth / 2}
        fill={backgroundColor || 'white'}
        stroke="transparent"
      />
      <ScoreText
        id={E2eTestElementId.NET_SCORE}
        dy="0.35em"
        dx="-0.27em"
        style={{
          fontSize,
          fill: isActive ? scoreTextColor(props.value, negativeColor, positiveColor) : BouColor.TOOL_TEXT_GRAY,
        }}
      >
        {formattedScore}
      </ScoreText>
    </g>
  );
}

const Root = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export function NetScoreBlock(props: NetScoreRowProps) {
  const { desktopMode } = useImpactChartContext();
  const { full: width } = useImpactChartWidths();
  const fontSizes = useImpactChartFontSizes();
  const height = fontSizes.netScore * 1.65;
  return (
    <Root style={{ width }}>
      <svg width={width} height={height} style={{ display: 'block' }}>
        <NetScoreRowMiddlePart {...props} />
      </svg>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
      {(desktopMode || true) && <NetScoreRowRightPart {...props} />}
    </Root>
  );
}
